import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  api,
  getToken,
  getStoredUser,
  setToken,
  setStoredUser,
  clearToken,
  clearStoredUser,
} from "@/lib/api";
import { fetchCompanyTz } from "@/lib/format";
import { toast } from "sonner";

const AuthCtx = createContext(null);

// Auto sign-out after this much inactivity.
const IDLE_MS = 4 * 60 * 60 * 1000; // 4 hours
const ACT_KEY = "borehub_last_activity";
const markActivity = () => { try { localStorage.setItem(ACT_KEY, String(Date.now())); } catch { /* ignore */ } };
const lastActivity = () => { const v = Number(localStorage.getItem(ACT_KEY)); return Number.isFinite(v) && v > 0 ? v : 0; };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const throttleRef = useRef(0);

  useEffect(() => {
    const tok = getToken();
    const u = getStoredUser();
    if (tok && u) {
      const last = lastActivity();
      if (last && Date.now() - last > IDLE_MS) {
        // Session went idle while the tab was closed — sign out.
        clearToken();
        clearStoredUser();
      } else {
        setUser(u);
        markActivity();
        fetchCompanyTz();
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setToken(r.access_token);
    setStoredUser(r.user);
    markActivity();
    setUser(r.user);
    fetchCompanyTz();
  };

  const register = async (companyName, name, email, password) => {
    const r = await api.post("/auth/register", { company_name: companyName, name, email, password });
    setToken(r.access_token);
    setStoredUser(r.user);
    markActivity();
    setUser(r.user);
  };

  const changePassword = async (currentPassword, newPassword) => {
    const r = await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
    setToken(r.access_token);
    setStoredUser(r.user);
    markActivity();
    setUser(r.user);
  };

  const logout = () => {
    clearToken();
    clearStoredUser();
    setUser(null);
  };

  // When any API call returns 401 (expired/invalid session), reset auth state cleanly
  // and tell the user — the Protected routes then redirect to /login (no broken state).
  useEffect(() => {
    const onUnauthorized = () => {
      clearToken();
      clearStoredUser();
      setUser((u) => {
        if (u) toast.error("Your session expired — please sign in again.");
        return null;
      });
    };
    window.addEventListener("auth:unauthorized", onUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", onUnauthorized);
  }, []);

  // Inactivity auto-logout: track interaction, and check on an interval + on tab focus.
  useEffect(() => {
    if (!user) return undefined;
    const bump = () => {
      const now = Date.now();
      if (now - throttleRef.current > 15000) { throttleRef.current = now; markActivity(); }
    };
    const check = () => { if (lastActivity() && Date.now() - lastActivity() > IDLE_MS) logout(); };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    const iv = setInterval(check, 30000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
      clearInterval(iv);
    };
  }, [user]);

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, changePassword, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

// Centralised role permissions (mirrors mobile usePerms).
export function usePerms() {
  const { user } = useAuth();
  const role = user?.role;
  const isGuest = role === "Guest";
  const isMechanic = role === "Mechanic";
  const isAdmin = role === "Admin" || !!user?.is_master;
  return {
    role,
    isGuest,
    isMechanic,
    isAdmin,
    canEdit: !isGuest,
    canManage: !isGuest && !isMechanic,
    canDelete: !isGuest && !isMechanic,
    canExport: !isGuest,
  };
}
